import dayjs from 'dayjs';
import ShelfController from '../../core/shelf.controller';
import Constants from '../../data/constants';
import { User } from '../../data';
import { canAccess, canSeePrices } from '../../core/access';

// True when an axios request was aborted (superseded by a newer one) — not a real error.
const isCanceled = (e) => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';

export const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
export const ROLE_OPTIONS = Object.values(Constants.USER_TYPES).map((v) => ({ label: cap(v), value: v }));

export const DEFAULT_FILTERS = { userType: [], userId: [], tagId: [], clientId: [] };

class UsoDeShelfController extends ShelfController {
    async initialize() {
        if (!this.isAuthenticated) {
            this.setState({ loading: false });
            this.navigateLogin();
            return;
        }

        const user = User.getInstance();
        if (!canAccess(user?.userType, 'uso-de-shelf')) {
            this.setState({ loading: false, accessDenied: true });
            return;
        }

        try {
            const [users, tagsRes, clients] = await Promise.all([
                this.data.fetchUsers().catch(() => []),
                this.data.fetchTags().catch(() => null),
                this.data.fetchClients().catch(() => [])
            ]);
            const usersList = (users || []).filter((u) => u?._id && u?.name);
            const clientOptions = (clients || [])
                .filter((c) => c?._id && c?.name)
                .map((c) => ({ label: c.name, value: c._id }))
                .sort((a, b) => a.label.localeCompare(b.label));
            // Only tags that can be assigned to a client, user, or store are meaningful here.
            const tagOptions = (tagsRes?.tags || [])
                .filter((t) => t?._id && (t.applicableModels || []).some((m) => ['Client', 'User', 'Store'].includes(m)))
                .map((t) => ({ label: t.name, value: t._id, color: t.color }));
            this.setState({ users: usersList, tagOptions, clientOptions, loading: false });
            await this.loadAll();
        } catch (err) {
            this.setState({ loading: false, error: true });
        }
    }

    retry() {
        this.setState({ loading: true, error: false }, () => this.initialize());
    }

    loadAll() {
        this.loadUsage();
    }

    buildParams() {
        const { filters, dateRange } = this.state;
        const q = {};
        if (filters.userType?.length) q.userType = filters.userType.join(',');
        if (filters.userId?.length) q.userId = filters.userId.join(',');
        if (filters.tagId?.length) q.tagId = filters.tagId.join(',');
        if (filters.clientId?.length) q.clientId = filters.clientId.join(',');
        if (dateRange?.[0] && dateRange?.[1]) {
            q.startDate = dateRange[0].startOf('day').toISOString();
            q.endDate = dateRange[1].endOf('day').toISOString();
        }
        return q;
    }

    async loadUsage() {
        // Abort the previous request so a fast filter change ends on the latest selection.
        this._usageAbort?.abort();
        const abort = new AbortController();
        this._usageAbort = abort;
        this.setState({ dataLoading: true });
        try {
            const res = await this.data.fetchShelfUsage(this.buildParams(), abort.signal);
            if (this._usageAbort !== abort) return; // superseded
            this.setState({ data: res?.data || null, dataLoading: false });
        } catch (err) {
            if (this._usageAbort !== abort || isCanceled(err)) return;
            this.props.onMessage?.('error', 'No se pudo cargar el uso de Shelf');
            this.setState({ dataLoading: false });
        }
    }

    setFilter(key, value) {
        this.setState(
            (prev) => {
                const filters = { ...prev.filters, [key]: value };
                // When the rol filter changes, drop any selected users that no longer match a rol.
                if (key === 'userType') {
                    const roles = value || [];
                    if (roles.length && filters.userId?.length) {
                        const allowed = new Set((prev.users || []).filter((u) => roles.includes(u.userType)).map((u) => u._id));
                        filters.userId = filters.userId.filter((id) => allowed.has(id));
                    }
                }
                return { filters };
            },
            () => this.loadAll()
        );
    }

    setDateRange(dateRange) {
        this.setState({ dateRange }, () => this.loadAll());
    }

    clearFilters() {
        this.setState({ filters: { ...DEFAULT_FILTERS }, dateRange: [dayjs().subtract(90, 'day'), dayjs()] }, () => this.loadAll());
    }

    // True when the current data is coordinate-update activity (location role). Prices don't apply.
    get coordinateMode() {
        return !!this.state.data?.coordinateMode;
    }

    // Hide all price info when in coordinate mode OR for roles without "Ver precios".
    get hidePrices() {
        return this.coordinateMode || !canSeePrices(User.getInstance()?.userType);
    }

    // Usuario options scoped to the selected rol(es) — empty rol = every user.
    userOptions() {
        const { users, filters } = this.state;
        const roles = filters.userType || [];
        return (users || []).filter((u) => !roles.length || roles.includes(u.userType)).map((u) => ({ label: u.name, value: u._id }));
    }

    // KPI numbers for the summary row.
    usageSummary() {
        const { data } = this.state;
        const totals = data?.totals || { visits: 0, users: 0, clients: 0, prices: 0 };
        const activeDays = (data?.daily || []).length;
        return {
            totals,
            activeDays,
            avgPerDay: activeDays ? Math.round(totals.visits / activeDays) : 0,
            drillStores: !!data?.drillStores,
            coordinateMode: !!data?.coordinateMode
        };
    }
}

export default UsoDeShelfController;
